import {
  Button,
  Container,
  Flex,
  Heading,
  Section,
  Stack,
} from '@grubhubprod/gh-cookbook-react';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Breadcrumbs } from 'src/components/Breadcrumbs/Breadcrumbs';
import { ChildAccountCreationHeader } from 'src/components/enterprise/Parents/Children/ParentChildAccountController/ChildAccountCreationHeader/ChildAccountCreationHeader';
import { createTableErrorsText } from 'src/components/enterprise/Parents/Children/ParentChildAccountController/ChildAccountTable/transformationFunctions/createTableErrorsText';
import { CreatingChildAccountsModal } from 'src/components/enterprise/Parents/Children/ParentChildAccountController/CreatingChildAccountsModal/CreatingChildAccountsModal';
import { useChildCorpNameErrors } from 'src/components/enterprise/Parents/Children/ParentChildAccountController/hooks/useChildCorpNameErrors/useChildCorpNameErrors';
import { useCreateChildAccounts } from 'src/components/enterprise/Parents/Children/ParentChildAccountController/hooks/useCreateChildAccounts/useCreateChildAccounts';
import { useGetParentCorpData } from 'src/components/enterprise/Parents/Children/ParentChildAccountController/hooks/useGetParentCorpData';
import { ChildAccountDisplayObject } from 'src/components/enterprise/Parents/Children/ParentChildAccountController/types';
import PageHeader from 'src/components/enterprise/layout/PageHeader';
import PageSection from 'src/components/enterprise/layout/PageSection';
import { pushSuccess } from 'src/utilities/notifications';
import { MAX_NUMBER_OF_CHILD_ACCOUNTS } from 'src/components/enterprise/Parents/constants';
import ChildAccountNameTextarea from './ChildAccountNameTextarea/ChildAccountNameTextarea';
import { ChildAccountDisplay } from './ChildAccountTable/ChildAccountDisplay/ChildAccountDisplay';
import { ChildAccountEdit } from './ChildAccountTable/ChildAccountEdit/ChildAccountEdit';
import { ChildAccountRow } from './ChildAccountTable/ChildAccountRowWrapper/ChildAccountRow';
import ChildAccountTable from './ChildAccountTable/ChildAccountTable';
import { makeDisplayObjects } from './ChildAccountTable/transformationFunctions/makeDisplayObjects';
import { makeOneAccountEditable } from './ChildAccountTable/transformationFunctions/makeOneAccountEditable';
import { removeName } from './ChildAccountTable/transformationFunctions/removeName';
import { resetChildAccountsDisplay } from './ChildAccountTable/transformationFunctions/resetChildAccountsDisplay';
import { updateChildAccountName } from './ChildAccountTable/transformationFunctions/updateChildAccountName';

export default function ChildAccountCreationController(): JSX.Element {
  const { corpid: corpId } = useParams<{ corpid: string }>();

  const [childAccounts, setChildAccounts] = useState<
    ChildAccountDisplayObject[]
  >([]);

  const {
    errorsByName,
    revalidateForChildAccounts,
    isLoading: isChildCorpNamesErrorsLoading,
  } = useChildCorpNameErrors({
    parentCorpId: corpId,
  });

  const onClickAdd = (names: string[]) => {
    const uniqueChildAccounts = makeDisplayObjects(names);
    const numberOfAccountsAdded = uniqueChildAccounts.length;

    const message =
      numberOfAccountsAdded === 1
        ? 'account has been added'
        : 'accounts have been added';

    pushSuccess(`${numberOfAccountsAdded} ${message}`);
    setChildAccounts((prevState) => {
      const nextState = [...prevState, ...uniqueChildAccounts];

      revalidateForChildAccounts(nextState);

      return nextState;
    });
  };

  const parentAccountLink = `/groupadmin/parents/${corpId}`;

  const navigateToParentDetails = (numberOfChildAccountsCreated?: number) => {
    const queryParams = numberOfChildAccountsCreated
      ? `?numberOfChildAccountsCreated=${numberOfChildAccountsCreated}`
      : '';
    window.location.href = `${window.location.origin}${parentAccountLink}${queryParams}`;
  };

  const { isLoading, data } = useGetParentCorpData(corpId);

  const { mutate, isLoading: isCreateChildAccountsLoading } =
    useCreateChildAccounts({
      onSuccess: () => {
        navigateToParentDetails(childAccounts.length);
      },
    });

  if (isLoading || !data) {
    return null;
  }

  const parentAccountName = data?.name || '';

  const onClickEdit = (keyOfNameBeingEdited: string) => {
    const updatedAccounts = makeOneAccountEditable(
      childAccounts,
      keyOfNameBeingEdited
    );

    setChildAccounts(updatedAccounts);
  };

  const onClickUpdate = (newName: string, uniqueDisplayKey: string) => {
    const updatedAccounts = updateChildAccountName(
      childAccounts,
      newName,
      uniqueDisplayKey
    );

    setChildAccounts(updatedAccounts);
    revalidateForChildAccounts(updatedAccounts);
  };

  const onCancelEdit = () => {
    setChildAccounts(resetChildAccountsDisplay(childAccounts));
  };

  const onClickDelete = (uniqueDisplayKey: string) => {
    const updatedAccounts = removeName(childAccounts, uniqueDisplayKey);

    setChildAccounts(updatedAccounts);
    revalidateForChildAccounts(updatedAccounts);
  };

  const isAddChildAccountsButtonDisabled =
    isChildCorpNamesErrorsLoading ||
    Object.keys(errorsByName).length > 0 ||
    childAccounts.length === 0;

  const handleAddChildAccountsClick = () => {
    const childAccountNames = childAccounts.map(({ name }) => name);
    mutate({ childAccountNames, parentCorpId: corpId });
  };

  const tableErrorsText = createTableErrorsText(errorsByName, childAccounts);
  return (
    <Container variant="default">
      <Section inset="sm">
        <PageHeader>
          <Stack y="lg">
            <Breadcrumbs
              breadcrumbs={[
                {
                  label: 'Parent accounts',
                  href: '/groupadmin/parents',
                },
                { label: 'Details', href: parentAccountLink },
              ]}
            />
          </Stack>
          <Stack y="lg">
            <Heading variant="h2">{`Add child accounts to ${parentAccountName}`}</Heading>
          </Stack>
        </PageHeader>

        <Stack y="lg">
          <PageSection>
            <ChildAccountCreationHeader />
            <Stack y="lg">
              <ChildAccountNameTextarea
                parentAccountName={parentAccountName}
                onAdd={onClickAdd}
              />
            </Stack>
            <Stack y="md">
              <ChildAccountTable
                currentNumberOfChildAccounts={childAccounts.length}
                maximumAllowedAccounts={MAX_NUMBER_OF_CHILD_ACCOUNTS}
                tableErrorsText={tableErrorsText}
              >
                {childAccounts.map(
                  ({ name, uniqueDisplayKey, isBeingEdited, isDisabled }) => {
                    return (
                      <ChildAccountRow key={uniqueDisplayKey}>
                        {isBeingEdited ? (
                          <ChildAccountEdit
                            childAccountName={name}
                            uniqueDisplayKey={uniqueDisplayKey}
                            onClickUpdate={onClickUpdate}
                            onCancelEdit={onCancelEdit}
                          />
                        ) : (
                          <ChildAccountDisplay
                            changeToEditMode={onClickEdit}
                            uniqueDisplayKey={uniqueDisplayKey}
                            childAccountName={name}
                            isDisabled={isDisabled}
                            removeName={onClickDelete}
                            errorKeys={errorsByName[name]}
                          />
                        )}
                      </ChildAccountRow>
                    );
                  }
                )}
              </ChildAccountTable>
            </Stack>
          </PageSection>
        </Stack>
        <Flex justifyContent="flex-end">
          <Stack x="sm">
            <Button
              variant="secondary"
              buttonSize="medium"
              onClick={() => navigateToParentDetails()}
            >
              Cancel
            </Button>
          </Stack>
          <Button
            buttonSize="medium"
            onClick={handleAddChildAccountsClick}
            disabled={isAddChildAccountsButtonDisabled}
          >
            Add child account(s)
          </Button>
        </Flex>
      </Section>
      {isCreateChildAccountsLoading && <CreatingChildAccountsModal />}
    </Container>
  );
}
